<template>
    <section>
        <div class="container">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <h3 class="page-title mb-0">Nutzungsstatistik</h3>
                <div class="d-flex align-items-center gap-2">
                    <label class="mb-0 small text-muted" for="statsWindow">Zeitraum</label>
                    <select id="statsWindow" v-model.number="windowDays" class="form-select form-select-sm window-select" @change="loadStats">
                        <option :value="7">7 Tage</option>
                        <option :value="30">30 Tage</option>
                        <option :value="90">90 Tage</option>
                        <option :value="365">1 Jahr</option>
                        <option :value="730">2 Jahre</option>
                        <option :value="1095">3 Jahre</option>
                    </select>
                    <button class="btn btn-outline-primary btn-sm" @click="loadStats" :disabled="loading">
                        Aktualisieren
                    </button>
                </div>
            </div>

            <p class="hint">
                Geräte-Listen zählen <strong>eindeutige Nutzer</strong> im Zeitraum
                (wer Desktop und Handy nutzt, erscheint in beiden Klassen).
                Logins und der mobile Anteil zählen <strong>Aktivität</strong> (Logins und Heartbeats).
                Geräteinfos kommen aus Client Hints (wo verfügbar) und dem User-Agent.
                OS- und Browser-Listen zeigen die <strong>Hauptversion</strong>
                (z.&nbsp;B. Safari&nbsp;18, iOS&nbsp;18); in den Tabellen steht die volle Version,
                soweit der Browser sie liefert.
                Safari auf iOS&nbsp;26+ friert den OS-Teil des User-Agents auf 18.x ein —
                die Statistik nimmt dann die Safari-Version (entspricht der iOS-Version).
                Exakte Modelle (z.&nbsp;B. „iPhone&nbsp;16 Pro“) kommen in der nativen App
                vom Gerät; im Web-Portal nur, wenn der Browser sie liefert —
                auf iOS/Safari oft nur „iPhone“. Ereignisse werden nach
                <strong>{{ stats?.retentionDays || 1095 }} Tagen (~3 Jahre)</strong> automatisch gelöscht
                (aktuell {{ stats?.eventCount ?? '…' }} Einträge).
            </p>

            <div v-if="loading && !stats" class="text-muted">Lade …</div>

            <div v-else-if="stats" class="row" :class="{ 'is-refreshing': loading }">
                <div class="col-md-3 mb-3">
                    <div class="card metric-card">
                        <div class="metric-label">Logins ({{ windowLabel }})</div>
                        <div class="metric-value">{{ stats.logins.success }}</div>
                        <div class="metric-sub">fehlgeschlagen: {{ stats.logins.failed }}</div>
                    </div>
                </div>
                <div class="col-md-3 mb-3">
                    <div class="card metric-card">
                        <div class="metric-label">Aktive Nutzer ({{ windowLabel }})</div>
                        <div class="metric-value">{{ stats.activeUsers.inWindow }}</div>
                        <div class="metric-sub" v-if="windowDays > 7">
                            letzte 7 Tage: {{ stats.activeUsers.last7Days }}
                        </div>
                        <div class="metric-sub" v-else>im gewählten Zeitraum</div>
                    </div>
                </div>
                <div class="col-md-3 mb-3">
                    <div class="card metric-card">
                        <div class="metric-label">Neue Protokolle ({{ windowLabel }})</div>
                        <div class="metric-value">{{ stats.protocols.inWindow }}</div>
                        <div class="metric-sub">im gewählten Zeitraum</div>
                    </div>
                </div>
                <div class="col-md-3 mb-3">
                    <div class="card metric-card">
                        <div class="metric-label">Mobile Anteil ({{ windowLabel }})</div>
                        <div class="metric-value">{{ stats.mobileShare }}%</div>
                        <div class="metric-sub">
                            {{ stats.mobileActivity?.mobile ?? 0 }} von {{ stats.mobileActivity?.total ?? 0 }} Logins/Heartbeats
                        </div>
                    </div>
                </div>

                <div class="col-lg-6 mb-4">
                    <div class="card panel-card">
                        <div class="card-header">
                            <h5>Geräte</h5>
                            <p class="card-header-note">Anzahl Nutzer im Zeitraum</p>
                        </div>
                        <div class="card-body">
                            <h6>Klasse</h6>
                            <ul class="stat-list">
                                <li v-for="row in stats.devices.byClass" :key="'c-' + row.name">
                                    <span>{{ labelDeviceClass(row.name) }}</span>
                                    <span class="count">{{ row.count }}</span>
                                </li>
                                <li v-if="!stats.devices.byClass.length" class="empty">Keine Daten</li>
                            </ul>
                            <h6>Betriebssystem</h6>
                            <ul class="stat-list">
                                <li v-for="row in stats.devices.byOs" :key="'o-' + row.name">
                                    <span>{{ row.name }}</span>
                                    <span class="count">{{ row.count }}</span>
                                </li>
                                <li v-if="!stats.devices.byOs.length" class="empty">Keine Daten</li>
                            </ul>
                            <h6>Browser</h6>
                            <ul class="stat-list">
                                <li v-for="row in stats.devices.byBrowser" :key="'b-' + row.name">
                                    <span>{{ row.name }}</span>
                                    <span class="count">{{ row.count }}</span>
                                </li>
                                <li v-if="!stats.devices.byBrowser.length" class="empty">Keine Daten</li>
                            </ul>
                            <h6>Modell</h6>
                            <ul class="stat-list">
                                <li v-for="row in stats.devices.byModel" :key="'m-' + row.name">
                                    <span>{{ row.name }}</span>
                                    <span class="count">{{ row.count }}</span>
                                </li>
                                <li v-if="!stats.devices.byModel.length" class="empty">Keine Daten</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div class="col-lg-6 mb-4">
                    <div class="card panel-card">
                        <div class="card-header">
                            <h5>Protokoll-Nutzung</h5>
                            <p class="card-header-note">Im gewählten Zeitraum</p>
                        </div>
                        <div class="card-body">
                            <h6>Nach Typ</h6>
                            <ul class="stat-list">
                                <li v-for="row in stats.protocols.byType" :key="'t-' + row.name">
                                    <span>{{ row.name }}</span>
                                    <span class="count">{{ row.count }}</span>
                                </li>
                                <li v-if="!stats.protocols.byType.length" class="empty">Keine Daten</li>
                            </ul>
                            <h6>Nach Status</h6>
                            <ul class="stat-list">
                                <li v-for="row in stats.protocols.byStatus" :key="'s-' + row.name">
                                    <span>{{ row.name }}</span>
                                    <span class="count">{{ row.count }}</span>
                                </li>
                                <li v-if="!stats.protocols.byStatus.length" class="empty">Keine Daten</li>
                            </ul>
                            <h6>Nach Benutzer (Top)</h6>
                            <ul class="stat-list">
                                <li v-for="row in stats.protocols.byUser" :key="'u-' + row.name">
                                    <span>{{ row.name }}</span>
                                    <span class="count">{{ row.count }}</span>
                                </li>
                                <li v-if="!stats.protocols.byUser.length" class="empty">Keine Daten</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div class="col-12 mb-4">
                    <div class="card panel-card day-chart-card">
                        <div class="card-header">
                            <h5>Neue Protokolle / Tag (Zeitraum)</h5>
                            <p class="card-header-note">Im gewählten Zeitraum</p>
                        </div>
                        <div class="card-body">
                            <div v-if="protocolDayChart.max > 0" class="day-chart-block">
                                <div class="day-chart-with-axis" :class="{ 'has-counts': showDayCounts }">
                                    <div class="day-y-axis" aria-hidden="true">
                                        <span>{{ protocolDayChart.max }}</span>
                                        <span>0</span>
                                    </div>
                                    <div
                                        class="day-chart"
                                        :class="{ 'is-dense': protocolDayChart.rows.length > 60 }"
                                        role="img"
                                        :aria-label="protocolDayChartAria"
                                    >
                                        <div
                                            v-for="row in protocolDayChart.rows"
                                            :key="'d-' + row.day"
                                            class="day-col"
                                            :title="protocolDayTitle(row)"
                                        >
                                            <span v-if="showDayCounts" class="day-count">{{ row.count || '' }}</span>
                                            <div class="day-bar-track">
                                                <div
                                                    class="day-bar"
                                                    :class="{ 'is-zero': row.count === 0 }"
                                                    :style="{ height: protocolDayBarHeight(row.count) }"
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="day-axis">
                                    <span>{{ protocolDayChart.axis.start }}</span>
                                    <span v-if="protocolDayChart.axis.mid">{{ protocolDayChart.axis.mid }}</span>
                                    <span>{{ protocolDayChart.axis.end }}</span>
                                </div>
                            </div>
                            <p v-else class="chart-empty">Keine Daten</p>
                        </div>
                    </div>
                </div>

                <div class="col-lg-6 mb-4">
                    <div class="card panel-card">
                        <div class="card-header"><h5>Zuletzt gesehen</h5></div>
                        <div class="card-body table-wrap">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Gerät</th>
                                        <th>Wann</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in stats.activeUsers.lastSeen" :key="'ls-' + row.userLogin">
                                        <td>{{ row.userLogin }}</td>
                                        <td>
                                            {{ row.deviceModel || labelDeviceClass(row.deviceClass) }}
                                            <span class="text-muted">· {{ row.os }} · {{ row.browser }}</span>
                                        </td>
                                        <td>{{ formatDateTime(row.at) }}</td>
                                    </tr>
                                    <tr v-if="!stats.activeUsers.lastSeen.length">
                                        <td colspan="3" class="text-muted">Noch keine Aktivität im Zeitraum</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="col-lg-6 mb-4">
                    <div class="card panel-card">
                        <div class="card-header">
                            <h5>Letzte Logins</h5>
                            <p class="card-header-note">Je Nutzer und Gerät, letzter Login</p>
                        </div>
                        <div class="card-body table-wrap">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Gerät</th>
                                        <th>Wann</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, idx) in stats.logins.recent" :key="'lg-' + row.userLogin + '-' + idx">
                                        <td>{{ row.userLogin }}</td>
                                        <td>
                                            {{ row.deviceModel || labelDeviceClass(row.deviceClass) }}
                                            <span class="text-muted">· {{ row.os }} · {{ row.browser }}</span>
                                        </td>
                                        <td class="when-cell">
                                            <span class="when-at">{{ formatDateTime(row.at) }}</span>
                                            <span v-if="row.count > 1" class="login-count">{{ row.count }}×</span>
                                        </td>
                                    </tr>
                                    <tr v-if="!stats.logins.recent.length">
                                        <td colspan="3" class="text-muted">Noch keine Logins im Zeitraum</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import API_BACKEND_URL from '@/API_URL/api_back_end.js';
import { getToken } from '@/services/tokenStore';

export default {
    name: 'AdminStats',
    data() {
        return {
            toast: useToast(),
            loading: false,
            stats: null,
            windowDays: 30,
        };
    },
    computed: {
        windowLabel() {
            const labels = {
                7: '7 Tage',
                30: '30 Tage',
                90: '90 Tage',
                365: '1 Jahr',
                730: '2 Jahre',
                1095: '3 Jahre',
            };
            return labels[this.windowDays] || `${this.windowDays} Tage`;
        },
        protocolDayChart() {
            const source = this.stats?.protocols?.perDay || [];
            const counts = new Map(source.map((row) => [row.day, row.count]));
            const today = new Date();
            today.setHours(12, 0, 0, 0);
            const rows = [];
            const dayCount = this.stats?.windowDays || this.windowDays;
            for (let offset = dayCount - 1; offset >= 0; offset -= 1) {
                const date = new Date(today);
                date.setDate(date.getDate() - offset);
                const day = this.toDayKey(date);
                rows.push({ day, count: counts.get(day) || 0 });
            }
            const max = rows.reduce((highest, row) => Math.max(highest, row.count), 0);
            return {
                rows,
                max,
                axis: {
                    start: rows.length ? this.formatChartDate(rows[0].day) : '',
                    mid: rows.length > 14 ? this.formatChartDate(rows[Math.floor(rows.length / 2)].day) : '',
                    end: rows.length ? this.formatChartDate(rows[rows.length - 1].day) : '',
                },
            };
        },
        protocolDayChartAria() {
            const { rows, max } = this.protocolDayChart;
            if (!max) {
                return 'Keine neuen Protokolle im Zeitraum';
            }
            return `${rows.length} Tage, höchstens ${max} neue Protokolle an einem Tag`;
        },
        showDayCounts() {
            return this.protocolDayChart.rows.length <= 31;
        },
    },
    methods: {
        authHeaders() {
            const token = getToken();
            return token ? { token } : {};
        },
        labelDeviceClass(value) {
            const map = {
                phone: 'Handy',
                tablet: 'Tablet',
                desktop: 'Desktop',
                unknown: 'Unbekannt',
            };
            return map[value] || value || 'Unbekannt';
        },
        formatDateTime(value) {
            if (!value) {
                return '';
            }
            return new Date(value).toLocaleString('de-AT');
        },
        toDayKey(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        formatChartDate(dayKey) {
            if (!dayKey) {
                return '';
            }
            const [year, month, day] = dayKey.split('-').map(Number);
            const options = { day: 'numeric', month: 'numeric' };
            if (this.windowDays > 90) {
                options.year = '2-digit';
            }
            return new Date(year, month - 1, day).toLocaleDateString('de-AT', options);
        },
        protocolDayTitle(row) {
            return `${this.formatChartDate(row.day)}: ${row.count}`;
        },
        protocolDayBarHeight(count) {
            const max = this.protocolDayChart.max;
            if (!max || count <= 0) {
                return '0%';
            }
            return `max(3px, ${(count / max) * 100}%)`;
        },
        async loadStats() {
            this.loading = true;
            try {
                const res = await axios.get(`${API_BACKEND_URL}admin/usage-stats`, {
                    headers: this.authHeaders(),
                    params: { days: this.windowDays },
                });
                this.stats = res.data;
            } catch (err) {
                const status = err.response?.status;
                if (status === 401 || status === 403) {
                    this.toast.warning('Keine Berechtigung für diesen Bereich.', { timeout: 3000 });
                    this.$router.push('/home');
                } else {
                    this.toast.warning('Statistik konnte nicht geladen werden.', { timeout: 3000 });
                }
            } finally {
                this.loading = false;
            }
        },
    },
    created() {
        if (this.$store.state.auth.user?.role !== 'administrator') {
            this.$router.push('/home');
            return;
        }
        this.loadStats();
    },
};
</script>

<style scoped>
section {
    margin-top: var(--app-header-height);
    padding: 0.85rem 1rem calc(var(--app-footer-height) + 0.5rem);
}

.page-title {
    color: #264271;
}

.hint {
    color: #555;
    font-size: 0.92rem;
    margin-bottom: 1.25rem;
}

.window-select {
    width: auto;
    min-width: 7rem;
}

.metric-card,
.panel-card {
    border-radius: 15px;
    border: 2px solid #c1c1c1;
    background: #fff;
    height: 100%;
    /* Header-/Body-Hintergründe sonst schneiden die abgerundeten Ecken ab */
    overflow: hidden;
}

.row.is-refreshing {
    opacity: 0.55;
    pointer-events: none;
}

.metric-card {
    padding: 1rem 1.1rem;
}

.metric-label {
    font-size: 0.9rem;
    color: #666;
}

.metric-value {
    font-size: 1.8rem;
    font-weight: 700;
    color: #264271;
    font-variant-numeric: tabular-nums;
}

.metric-sub {
    font-size: 0.85rem;
    color: #777;
}

.panel-card .card-header {
    background: #f7f8fa;
    border-bottom: 1px solid #ddd;
    border-top-left-radius: 13px;
    border-top-right-radius: 13px;
}

.panel-card .card-header h5 {
    margin: 0;
    font-size: 1.05rem;
}

.panel-card .card-header-note {
    margin: 0.2rem 0 0;
    font-size: 0.8rem;
    color: #777;
}

.panel-card .card-body {
    padding: 1rem 1.1rem;
}

.panel-card h6 {
    margin-top: 0.85rem;
    margin-bottom: 0.35rem;
    font-size: 0.9rem;
    color: #444;
}

.panel-card h6:first-child {
    margin-top: 0;
}

.stat-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.stat-list li {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.2rem 0;
    border-bottom: 1px solid #eee;
    font-size: 0.95rem;
}

.stat-list .count {
    font-family: monospace;
    font-weight: 600;
}

.stat-list .empty {
    color: #888;
    border-bottom: none;
}

.table-wrap {
    overflow-x: auto;
}

.table th {
    white-space: nowrap;
}

.when-cell {
    white-space: nowrap;
}

.when-cell .when-at {
    margin-right: 0.45rem;
}

.login-count {
    display: inline-block;
    white-space: nowrap;
    color: #777;
    font-variant-numeric: tabular-nums;
}

.day-chart-card {
    display: flex;
    flex-direction: column;
}

.day-chart-card .card-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.day-chart-block {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: clamp(11rem, 28vh, 24rem);
    width: 100%;
}

.day-chart-with-axis {
    flex: 1 1 auto;
    display: flex;
    align-items: stretch;
    gap: 0.45rem;
    min-height: 0;
}

.day-y-axis {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    font-size: 0.7rem;
    color: #888;
    font-variant-numeric: tabular-nums;
    padding: 0.15rem 0;
    min-width: 1.4rem;
}

.day-chart-with-axis.has-counts .day-y-axis {
    padding-top: 1.05rem;
}

.day-chart {
    flex: 1 1 auto;
    display: flex;
    align-items: stretch;
    gap: 3px;
    min-width: 0;
    width: 100%;
}

.day-chart.is-dense {
    gap: 0;
}

.day-col {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    cursor: default;
}

.day-col:hover .day-bar:not(.is-zero) {
    background: #315492;
}

.day-count {
    font-size: 0.65rem;
    line-height: 1.1;
    text-align: center;
    color: #264271;
    font-variant-numeric: tabular-nums;
    margin-bottom: 0.15rem;
    min-height: 1.1em;
}

.day-bar-track {
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    align-items: flex-end;
    min-height: 0;
}

.day-bar {
    width: 100%;
    background: #264271;
    border-radius: 2px 2px 0 0;
}

.day-bar.is-zero {
    height: 1px !important;
    background: #d5dae3;
    border-radius: 0;
}

.day-axis {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.4rem;
    font-size: 0.75rem;
    color: #777;
}

.chart-empty {
    color: #888;
    margin: 0;
    font-size: 0.95rem;
}
</style>
